/**
 * THE SHELF (doc 06, depth 1) — the four species a player meets first, and the
 * two that end their first descent.
 *
 * Every body here swims east; the render layer mirrors it for westbound fish.
 * Nothing in this file draws armour bands, motion streaks or a death: those are
 * doc 10 markers and builder transforms, applied identically to all nineteen
 * species so a player learns each word once (`markers.ts`, `fauna.ts`).
 *
 * The silhouette is the whole job. At 4 px per tile you know a fish by its
 * outline and its light, so no two species here share a shape: a comma, a
 * wedge with a jaw, a domed crab, a needle, a jaw on legs, a pair of wings.
 */

import type { CreatureDraw } from '../creature.ts';
import { bodyLean, gait, tailThrow } from '../creature.ts';
import { blob, dome, legs, teeth } from '../organic.ts';

/**
 * KRILL — a comma with a light on it.
 *
 * The smallest thing in the game and the one that arrives in thirties, so it is
 * drawn as the least possible animal: three rows of body and one glowing dot.
 * Attacking, it curls — the only tell available at this size.
 */
export const drawKrill: CreatureDraw = (s, c, ctx, p) => {
  const t = tailThrow(ctx);
  const bite = ctx.state === 'attack';
  const lean = bodyLean(ctx);
  blob(s, c.ax + 1 + lean, c.ay + (bite ? 1 : 0), 3, 2, p.ramp.back, p.ramp.belly);
  // the tail: one pixel, thrown further when swimming, tucked under when biting
  s.px(c.ax - 3 + lean, c.ay + (bite ? -2 : t), p.ramp.fin);
  if (bite) s.px(c.ax - 2, c.ay - 2, p.ramp.fin); // the curl
  s.px(c.ax + 3 + lean, c.ay - 1, p.glow);
};

/**
 * GNASHER — a wedge that is mostly opinion.
 *
 * Doc 06 calls it "standard-issue bad mood with fins", which is a design brief:
 * it is the baseline fish, so it gets the baseline shape and every other
 * species is defined against it. The jaw notch is closed at rest and a hinged
 * wedge when it bites.
 */
export const drawGnasher: CreatureDraw = (s, c, ctx, p) => {
  const t = tailThrow(ctx);
  const lean = bodyLean(ctx);
  blob(s, c.ax + lean, c.ay, 6, 3, p.ramp.back, p.ramp.belly);
  if (ctx.state === 'attack') {
    // hinged open around a dark throat — the wedge doubles in height
    s.fillRect(c.ax + 5, c.ay - 4, 4, 3, p.ramp.back, 'set');
    s.fillRect(c.ax + 5, c.ay + 1, 4, 3, p.ramp.belly, 'set');
    s.fillRect(c.ax + 6, c.ay - 1, 3, 2, p.ramp.maw, 'set');
    teeth(s, c.ax + 6, c.ay - 2, 3, 1, p.ramp.bone);
    teeth(s, c.ax + 6, c.ay + 1, 3, 1, p.ramp.bone);
  } else {
    s.px(c.ax + 6, c.ay, p.ramp.maw);
    s.px(c.ax + 5, c.ay + 1, p.ramp.belly);
  }
  s.vline(c.ax - 7 + lean, c.ay - 2 + t, c.ay + 2 + t, p.ramp.fin, 'set');
  for (let k = 0; k < 3; k++) s.vline(c.ax - 2 + k, c.ay - 4 - k + (t > 0 ? 1 : 0), c.ay - 3, p.ramp.back, 'set');
  s.px(c.ax + 4 + lean, c.ay - 1, p.glow);
};

/**
 * SHELLBACK — a crab that read about walls and became one.
 *
 * The only domed silhouette at this depth: a carapace with nothing above it and
 * legs under it. Its armour used to be three hand-drawn bands; it is now the
 * universal marker, which is the point of graduating.
 */
export const drawShellback: CreatureDraw = (s, c, ctx, p) => {
  const rear = ctx.state === 'attack' ? 2 : 0;
  // Broad and LOW, because the Gnasher is narrow and tall: the two fish a
  // player meets first were the closest silhouette pair in the game at 0.71
  // IoU, and a crab that is merely a different blob is not a different animal.
  dome(s, c.ax, c.ay - 1 - rear, 10, 3, p.ramp.back, p.ramp.belly, 0.9);
  s.hline(c.ax - 10, c.ax + 9, c.ay + 1 - rear, p.ramp.belly, 'set'); // the skirt
  legs(s, c.ax, c.ay + 2, [-8, -5, -1, 3, 7], 2, 3, ctx.phase, p.ramp.fin, gait(ctx));
  if (ctx.state === 'attack') {
    // reared up, with the claw out front where the wall is
    s.fillRect(c.ax + 9, c.ay - 3, 3, 2, p.ramp.fin, 'set');
    s.fillRect(c.ax + 10, c.ay, 3, 2, p.ramp.fin, 'set');
  }
  s.px(c.ax + 8, c.ay - 2 - rear, p.glow);
};

/**
 * SILVERDART — a needle that is late for something.
 *
 * Two rows tall and nothing else: the thinnest profile in the game, which is
 * how it reads as fast before the motion streaks even arrive. Attacking, it
 * stops undulating entirely and becomes a straight line.
 */
export const drawSilverdart: CreatureDraw = (s, c, ctx, p) => {
  const t = ctx.state === 'attack' ? 0 : tailThrow(ctx);
  const reach = ctx.state === 'attack' ? 2 : 0;
  // two rows of fish have nowhere to put a swim cycle, so the whole needle
  // surges: the body slides a pixel forward on each drive beat.
  const lean = bodyLean(ctx);
  s.hline(c.ax - 6 + lean, c.ax + 7 + reach + lean, c.ay, p.ramp.back, 'set');
  s.hline(c.ax - 5 + lean, c.ax + 6 + reach + lean, c.ay + 1, p.ramp.belly, 'set');
  s.px(c.ax - 7 + lean, c.ay - 1 + t, p.ramp.fin);
  s.px(c.ax - 7 + lean, c.ay + 1 - t, p.ramp.fin);
  if (reach > 0) s.px(c.ax + 9, c.ay, p.ramp.bone); // the point that goes in first
  s.px(c.ax + 5, c.ay, p.glow);
};

/**
 * OLD SNAPJAW (mini-boss) — a jaw that walks.
 *
 * Doc 06 gives it a grab every ten seconds, so the jaw is the entire read: an
 * armoured mass on four legs with a hinge at the front, and the hinge is wide
 * open in the pose the player sees while a tower is being held.
 */
export const drawSnapjaw: CreatureDraw = (s, c, ctx, p) => {
  const grab = ctx.state === 'attack';
  // The jaw is a THIRD of the animal, not a bump on the end of it. The first
  // draft gave it a body of radius 11 and a jaw six pixels long, and it read as
  // a zeppelin with feet — doc 06 calls it "a huge armoured jaw on legs", and
  // that only works if the jaw is the biggest thing about it.
  blob(s, c.ax - 6, c.ay - 2, 9, 6, p.ramp.back, p.ramp.belly);
  const gape = grab ? 7 : 3;
  s.fillRect(c.ax + 2, c.ay - gape - 3, 11, 4, p.ramp.back, 'set');
  s.fillRect(c.ax + 2, c.ay + gape - 1, 11, 4, p.ramp.belly, 'set');
  s.fillRect(c.ax + 3, c.ay - gape + 1, 9, gape * 2 - 2, p.ramp.maw, 'set');
  teeth(s, c.ax + 4, c.ay - gape + 1, 3, 3, p.ramp.bone);
  teeth(s, c.ax + 6, c.ay + gape - 2, 3, 3, p.ramp.bone);
  legs(s, c.ax, c.ay + 5, [-12, -8, -4, 0], 2, 3, ctx.phase, p.ramp.fin, gait(ctx));
  s.px(c.ax - 1, c.ay - 6, p.glow);
};

/**
 * REEF TYRANT (boss) — two wings and a whip.
 *
 * The widest silhouette on THE SHELF and the only one built around a gap:
 * Old Snapjaw is a solid mass with a hinge, this is two triangles with a spine
 * between them, so the two never read as the same animal in black at 32 px.
 * Doc 06 sends it after your economy — the dive pose is wings back, tail forward.
 */
export const drawTyrant: CreatureDraw = (s, c, ctx, p) => {
  const dive = ctx.state === 'attack';
  const beat = dive ? -3 : tailThrow(ctx);
  // Thin, swept and tapering. The first draft filled two right triangles that
  // met over the spine and the whole animal read as a hill: on a ray the GAP
  // between the wings is the silhouette, not the mass between them.
  for (let k = 0; k < 12; k++) {
    const h = Math.max(0, 3 - ((k / 4) | 0));
    const y = c.ay - 3 + ((k / 3) | 0) + (k > 6 ? beat : 0);
    const tone = k % 2 ? p.ramp.belly : p.ramp.back;
    s.vline(c.ax - 4 - k, y - h, y + h, tone, 'set');
    s.vline(c.ax + 4 + k, y - h, y + h, tone, 'set');
  }
  blob(s, c.ax, c.ay - 1, 4, 4, p.ramp.back, p.ramp.belly);
  s.hline(c.ax - 14, c.ax - 5, c.ay + (beat >> 1), p.ramp.fin, 'set');
  if (dive) {
    // the whip comes over the top: the tell that your smelter is next
    s.hline(c.ax + 5, c.ax + 13, c.ay - 7, p.ramp.fin, 'set');
    s.px(c.ax + 14, c.ay - 6, p.ramp.bone);
  }
  s.px(c.ax + 2, c.ay - 3, p.glow);
  s.px(c.ax - 1, c.ay - 3, p.glow);
};
